import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

import harvestLgSvg from '@/assets/icons/ui/harvest-lg.svg?raw';

import { useHarvestStore } from '@/stores/harvest-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { useHuntStore } from '@/stores/hunt-store';

function formatHarvestLabel(species: string, sex: string, antlerPoints?: number): string {
  if (species === 'Whitetail Deer') {
    if (sex === 'buck') {
      return antlerPoints ? `${antlerPoints}-Point Buck` : 'Buck';
    }
    if (sex === 'doe') return 'Doe';
    return 'Deer';
  }
  const sexLabel = sex === 'buck' ? 'Male ' : sex === 'doe' ? 'Female ' : '';
  return `${sexLabel}${species}`.trim();
}

// Confetti particle config
const PARTICLES = [
  { x: -60, y: -80, rotate: 15, color: '#c2440a' },
  { x: 60, y: -90, rotate: -20, color: '#e8824a' },
  { x: -100, y: -40, rotate: 45, color: '#a03008' },
  { x: 100, y: -50, rotate: -35, color: '#f0a070' },
  { x: -30, y: -110, rotate: 70, color: '#c2440a' },
  { x: 40, y: -100, rotate: -60, color: '#e8824a' },
];

export function HarvestCelebrationPage() {
  const { huntId, harvestId } = useParams<{ huntId: string; harvestId: string }>();
  const navigate = useNavigate();

  const { records } = useHarvestStore();
  const { waypoints } = useWaypointStore();
  const { currentHunt, loadHunt } = useHuntStore();

  const record = records.find((r) => r.id === harvestId);

  useEffect(() => {
    if (huntId) void loadHunt(huntId);
  }, [huntId, loadHunt]);

  // Auto-navigate after 6 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(`/hunt/${huntId ?? ''}`);
    }, 6000);
    return () => clearTimeout(timer);
  }, [huntId, navigate]);

  const locationName = record?.waypointId
    ? (waypoints.find((w) => w.id === record.waypointId)?.name ?? null)
    : null;

  const harvestLabel = record
    ? formatHarvestLabel(record.species, record.sex, record.antlerPoints)
    : 'Harvest';

  const _hunt = currentHunt;

  if (record == null || currentHunt == null) return null;

  return (
    /*
      (i)  Gate condition verbatim: !(record == null || currentHunt == null)
           — equivalently record != null && currentHunt != null

      (ii) useEffect-alignment interpretation:
           This page has no auth-gated mutation useEffect; useEffects are read-only loaders.
           The gate-met condition ensures data is present before rendering; useEffects that
           depend on the data are unaffected.

      (iii) Layout-neutrality rationale:
            The wrapper uses Tailwind v4's `contents` utility (display: contents) so it sits in
            the DOM for Playwright querying but does not participate in the CSS box tree,
            preserving the inner element's direct-child relationship to its parent and protecting
            iOS Safari PWA viewport-recalc defenses.

      (iv) Optional contract callouts:
           data-status intentionally omitted: gate is data presence, not authStatus.
           See ADR-035 / RULE-194..199.

      (v)  No-gate-page route-ready strategy:
           (a) Page has no natural loading gate — it renders directly from `return (` with
               optional-chained fallbacks rather than an early-return guard.
           (b) Wrapper is conditional on the data-loaded predicate per Shape B locked strategy
               (Option D): {<predicate> && <div className="contents" data-testid="route-ready">...</div>}.
           (c) Page returns null during the hydration window — no skeleton introduction.
               While data is absent, the page renders nothing; once the predicate is met, the
               full page renders inside the route-ready wrapper.
           (d) Page-specific UX rationale for not introducing a skeleton:
               HarvestCelebrationPage — the celebration animation is a one-shot, time-bounded
               experience; a skeleton state preceding it would undermine the effect.
    */
    <div className="contents" data-testid="route-ready">
    <div
      className="fixed inset-0 flex flex-col items-center justify-center bg-[#0d0b09] overflow-hidden"
      data-testid="harvest-celebration-page"
    >
      {/* Confetti particles */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden>
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              width: 8,
              height: 8,
              borderRadius: 2,
              backgroundColor: p.color,
              animation: `confetti-${i} 1s ease-out forwards`,
              // inline keyframe via style tag below
              transform: `translate(${p.x}px, ${p.y}px) rotate(${p.rotate}deg)`,
              opacity: 0,
            }}
          />
        ))}
      </div>

      {/* CSS keyframes for confetti — injected inline */}
      <style>{`
        @keyframes confetti-scatter {
          0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          100% { opacity: 0; }
        }
        [data-testid="harvest-celebration-page"] span {
          animation: confetti-scatter 1.4s ease-out forwards;
        }
      `}</style>

      {/* Harvest hero icon */}
      {/* SAFETY: harvestLgSvg is a static import from compiled SVG assets */}
      <span
        className="w-12 h-12 mb-6 flex items-center justify-center"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: harvestLgSvg }}
      />

      {/* Headline */}
      <h1
        className="font-bold text-4xl text-white mb-3 text-center px-6"
        data-testid="celebration-headline"
      >
        Harvest Logged
      </h1>

      {/* Species / sex / points */}
      <p
        className="text-xl text-[#e8824a] font-semibold mb-2 text-center px-6"
        data-testid="celebration-species"
      >
        {harvestLabel}
      </p>

      {/* Location */}
      {locationName && (
        <p
          className="text-base text-[#a08060] mb-8 text-center px-6"
          data-testid="celebration-location"
        >
          {locationName}
        </p>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-3 w-full max-w-xs px-6 mt-6">
        <Link
          to={`/hunt/${huntId ?? ''}`}
          className="w-full py-3 rounded-lg bg-[#c2440a] text-white font-semibold text-center text-base hover:bg-[#a03008] transition-colors"
          data-testid="view-hunt-btn"
        >
          View Hunt
        </Link>
        <button
          type="button"
          onClick={() => navigate(`/hunt/${huntId ?? ''}?logSit=true`)}
          className="w-full py-3 rounded-lg border border-[#3a2a1a] text-[#a08060] font-medium text-base hover:border-[#c2440a] hover:text-white transition-colors"
          data-testid="add-sit-btn"
        >
          Add Another Sit
        </button>
      </div>

      <p className="text-[#4a3828] text-sm mt-6">Auto-returning to hunt in a moment…</p>
    </div>
    </div>
  );
}
